import React, { useState } from "react";
import {
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import SEO from "../components/common/SEO";
import NavFilters from "../components/common/NavFilters";
import { getBreadcrumbSchema } from "../utils/structuredData";

const initialState = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  const structuredData = getBreadcrumbSchema(breadcrumbs);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setForm(initialState);
    }, 500);
  };

  const contactInfo = [
    {
      icon: <FaPhone className="text-2xl" />,
      title: "Phone",
      content: "+355 69 231 1606",
      link: "tel:+355692311606",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FaEnvelope className="text-2xl" />,
      title: "Email",
      content: "seapulsealbania@gmail.com",
      link: "mailto:seapulsealbania@gmail.com",
      color: "from-cyan-500 to-blue-400",
    },
    {
      icon: <FaMapMarkerAlt className="text-2xl" />,
      title: "Location",
      content: "Durrës, Albania",
      link: "#",
      color: "from-blue-400 to-cyan-600",
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Contact SeaPulse Albania | Book Boat Tours in Durrës"
        description="Contact SeaPulse Albania to book your boat tour in Durrës. Available via WhatsApp, phone & email. Located at Durrës Marina. Fast response, instant booking confirmation. +355 69 231 1606."
        keywords="contact SeaPulse Albania, book boat tour Durrës, boat tour booking Albania, WhatsApp boat tour Albania, Durrës boat tours contact, Albania sea tour inquiry, book speedboat Albania, reserve yacht Durrës, SeaPulse phone number, Durrës marina contact"
        canonical="/contact"
        structuredData={structuredData}
      />
      {/* Hero Banner */}
      <div className="relative h-[400px] md:h-[450px] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=2000&q=80"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover brightness-90 dark:brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-cyan-800/70 to-blue-600/60" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 max-w-4xl mx-auto">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2 rounded-full mb-6 border border-white/30">
            💬 Get In Touch
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white drop-shadow-2xl mb-4 leading-tight">
            Contact Us
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/95 max-w-2xl leading-relaxed drop-shadow-lg">
            Have questions? We're here to help you plan your perfect adventure
          </p>
        </div>
      </div>

      <NavFilters />

      {/* Contact Info Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 md:-mt-4 lg:mt-4 relative z-20 mb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {contactInfo.map((info, index) => (
            <a
              key={index}
              href={info.link}
              className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:border-cyan-400 dark:hover:border-cyan-500 hover:-translate-y-2"
            >
              <div
                className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${info.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                {info.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {info.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">{info.content}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Send Us a Message
            </h2>

            {submitted ? (
              <div className="bg-gradient-to-r from-green-50 to-cyan-50 dark:from-green-900/20 dark:to-cyan-900/20 border-2 border-green-500 dark:border-green-400 rounded-xl p-8 text-center">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Message Sent Successfully!
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Thank you for reaching out! We'll get back to you within 24
                  hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block mb-2 font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block mb-2 font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block mb-2 font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="+355 69 123 4567"
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block mb-2 font-semibold text-gray-700 dark:text-gray-200"
                  >
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-vertical transition-colors"
                    placeholder="Tell us about your travel plans, questions, or special requests..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 rounded-lg text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Send Message ✉️
                </button>
              </form>
            )}
          </div>

          {/* Social & Info Section */}
          <div className="space-y-8">
            {/* Social Media */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-8 border border-cyan-200 dark:border-gray-700">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Connect With Us
              </h3>
              <div className="flex flex-col gap-4">
                <a
                  href="https://wa.me/355692311606"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl hover:shadow-lg transition-all duration-300 group hover:-translate-x-2"
                >
                  <div className="p-3 bg-green-500 rounded-full group-hover:scale-110 transition-transform">
                    <FaWhatsapp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white">
                      WhatsApp
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Quick response
                    </div>
                  </div>
                </a>
                <a
                  href="https://www.instagram.com/seapulsealbania/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl hover:shadow-lg transition-all duration-300 group hover:-translate-x-2"
                >
                  <div className="p-3 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full group-hover:scale-110 transition-transform">
                    <FaInstagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white">
                      Instagram
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      @seapulsealbania
                    </div>
                  </div>
                </a>
                <a
                  href="https://facebook.com/seapulsealbania"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl hover:shadow-lg transition-all duration-300 group hover:-translate-x-2"
                >
                  <div className="p-3 bg-blue-600 rounded-full group-hover:scale-110 transition-transform">
                    <FaFacebook className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white">
                      Facebook
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Stay updated
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Operating Hours
              </h3>
              <div className="space-y-3 text-gray-600 dark:text-gray-300">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Monday - Friday</span>
                  <span>8:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Saturday - Sunday</span>
                  <span>7:00 AM - 9:00 PM</span>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm">
                    ✨ Tours depart daily during peak season (May - September)
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold mb-4">
                Need Immediate Assistance?
              </h3>
              <p className="mb-6 opacity-90">
                Our team is available 24/7 for emergency support and last-minute
                bookings.
              </p>
              <a
                href="tel:+355692311606"
                className="inline-block bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Call Now: +355 69 231 1606
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
